import { ENTRY_TITLE_CHROME_PX } from "./newEntryTypes";

/**
 * DOM measurement helpers for the collapsed entry grid.
 * Used by `useCompactEntryGridLayout` to decide how many columns fit without clipping horse names.
 */

/** Visible grid width, never larger than the viewport (avoids crushed overflow reporting a huge width). */
export const getGridContentWidthPx = (gridEl: HTMLElement): number => {
    const rectWidth = gridEl.getBoundingClientRect().width;
    const clientWidth = gridEl.clientWidth;
    // Prefer the laid-out box; never trust a collapsed 0 when the rect is known.
    const gridWidth = clientWidth > 0 ? clientWidth : rectWidth;
    // Clamp to the visible viewport so overflowed/crushed layouts can't report a huge width.
    const viewportWidth =
        typeof window !== "undefined" ? Math.min(window.innerWidth, document.documentElement.clientWidth || window.innerWidth) : gridWidth;
    if (gridWidth <= 0) return Math.max(0, viewportWidth);
    return Math.min(gridWidth, viewportWidth);
};

/**
 * Measure the widest individual word using a live DOM probe with the title's computed type styles.
 * Canvas measurement underestimates custom/monospace fonts and causes false "fits" → clipping.
 */
export const measureLongestWordWidthPx = (titleEl: HTMLElement): number => {
    const text = titleEl.textContent?.trim() ?? "";
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return 0;

    const cs = window.getComputedStyle(titleEl);
    const probe = document.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.style.position = "absolute";
    probe.style.left = "0";
    probe.style.top = "0";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.whiteSpace = "nowrap";
    probe.style.display = "inline-block";
    probe.style.width = "auto";
    probe.style.maxWidth = "none";
    probe.style.margin = "0";
    probe.style.padding = "0";
    probe.style.border = "0";
    probe.style.fontFamily = cs.fontFamily;
    probe.style.fontSize = cs.fontSize;
    probe.style.fontWeight = cs.fontWeight;
    probe.style.fontStyle = cs.fontStyle;
    probe.style.fontVariant = cs.fontVariant;
    probe.style.fontStretch = cs.fontStretch;
    probe.style.letterSpacing = cs.letterSpacing;
    probe.style.lineHeight = cs.lineHeight;
    probe.style.textTransform = cs.textTransform;

    titleEl.appendChild(probe);
    let maxWidth = 0;
    for (const word of words) {
        probe.textContent = word;
        maxWidth = Math.max(maxWidth, probe.getBoundingClientRect().width);
    }
    titleEl.removeChild(probe);
    return maxWidth;
};

/** Width the horse title is actually allowed to occupy inside a compact grid cell. */
export const getAvailableTitleWidthPx = (titleEl: HTMLElement): number => {
    // Prefer the compact slot's assigned width. The title column can inflate to the
    // unbroken word's intrinsic width when overflow is visible, which falsely reports
    // that the name "fits" and keeps too many columns (wonky layout after Add Another Entry).
    const slot = titleEl.closest("[data-compact-entry-slot]") as HTMLElement | null;
    if (slot) {
        const slotWidth = slot.getBoundingClientRect().width;
        if (slotWidth > 0) {
            return Math.max(0, slotWidth - ENTRY_TITLE_CHROME_PX);
        }
    }
    const column = titleEl.closest("[data-entry-title-column]") as HTMLElement | null;
    if (column && column.clientWidth > 0) {
        return column.clientWidth;
    }
    return 0;
};

/**
 * True when any single word in the horse title is wider than the title column.
 * Multi-word names may wrap; unbroken words must fit or columns are reduced.
 * Unknown/zero width is treated as covered so we never default to a crushed multi-column layout.
 */
export const isHorseTitleCovered = (titleEl: HTMLElement): boolean => {
    const availableWidth = getAvailableTitleWidthPx(titleEl);
    if (availableWidth <= 0) return true;

    return measureLongestWordWidthPx(titleEl) > availableWidth - 8;
};
